<template>
  <div class="flex h-full grow flex-col">
    <main class="flex-1 px-4 sm:px-10 py-8">
      <div class="mx-auto flex w-full flex-col gap-8">
        <!-- Header -->
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="flex flex-col gap-1">
            <h1 class="text-slate-900 dark:text-slate-100 text-2xl sm:text-3xl font-bold leading-tight tracking-tight">
              Estimación por equipo
            </h1>
            <p class="text-slate-600 dark:text-slate-300 text-sm sm:text-base font-normal leading-normal">
              Para realizar cambios en esta estimación, haz clic en el botón 'Editar'.
            </p>
          </div>
          <div class="flex items-center gap-3">
            <button 
              @click="exportToPDF" 
              class="flex items-center justify-center gap-2 min-w-[84px] cursor-pointer rounded-lg h-10 px-4 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-sm font-semibold leading-normal shadow-sm hover:bg-orange-200 dark:hover:bg-orange-800/50 transition-colors border border-orange-200 dark:border-orange-700"
            >
              <span class="material-icons-outlined text-base">picture_as_pdf</span>
              <span class="truncate">Exportar PDF</span>
            </button>
            <router-link 
              :to="`/team-assignment?edit=${budget.id}`"
              class="flex items-center justify-center gap-2 min-w-[84px] cursor-pointer rounded-lg h-10 px-4 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-100 text-sm font-semibold leading-normal shadow-sm hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            >
              <span class="material-icons-outlined text-base">edit</span>
              <span class="truncate">Editar</span>
            </router-link>
          </div>
        </div>

        <!-- Budget Information & Project Information -->
        <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
          <h2 class="text-slate-900 dark:text-slate-100 text-lg font-semibold mb-4 ">Información del proyecto</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="text-gray-600 dark:text-slate-300 text-sm font-medium leading-normal">Nombre del Cliente</label>
              <p class="text-gray-800 dark:text-slate-200 text-sm font-normal leading-normal">{{ budget.clientName || 'No especificado' }}</p>
            </div>
            <div>
              <label class="text-gray-600 dark:text-slate-300 text-sm font-medium leading-normal">JIRA Ticket</label>
              <p class="text-gray-800 dark:text-slate-200 text-sm font-normal leading-normal">
                <a v-if="budget.jiraTicket" 
                   :href="budget.jiraTicket" 
                   target="_blank" 
                   class="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 hover:underline">
                  {{ budget.jiraTicket }}
                </a>
                <span v-else class="text-gray-400 dark:text-slate-500">No especificado</span>
              </p>
            </div>
            <div>
              <label class="text-gray-600 dark:text-slate-300 text-sm font-medium leading-normal">Quien arma la estimación</label>
              <p class="text-gray-800 dark:text-slate-200 text-sm font-normal leading-normall">{{ budget.projectName || 'No especificado' }}</p>
            </div>

            <div>
              <label class="text-gray-600 dark:text-slate-300 text-sm font-medium leading-normal">URL Intranet</label>
              <p class="text-gray-800 dark:text-slate-200 text-sm font-normal leading-normal">
                <a v-if="budget.intranetUrl" :href="budget.intranetUrl" target="_blank" class="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 hover:underline">
                  {{ budget.intranetUrl }}
                </a>
                <span v-else class="text-gray-400 dark:text-slate-500">No especificado</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Team Assignment Details -->
        <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div class="p-6 border-b border-slate-200 dark:border-slate-700">
            <h2 class="text-slate-900 dark:text-slate-100 text-lg font-semibold">Detalles de Asignación del Equipo</h2>
          </div>
          
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/30 border-b border-orange-200 dark:border-orange-700">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-orange-700 dark:text-orange-300 uppercase tracking-wider">Persona</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-orange-700 dark:text-orange-300 uppercase tracking-wider">Equipo</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-orange-700 dark:text-orange-300 uppercase tracking-wider">Rol</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-orange-700 dark:text-orange-300 uppercase tracking-wider">Duración</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-orange-700 dark:text-orange-300 uppercase tracking-wider">Días</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-orange-700 dark:text-orange-300 uppercase tracking-wider">Asignación %</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-orange-700 dark:text-orange-300 uppercase tracking-wider">Horas Estimadas</th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                <tr v-for="(member, index) in teamMembers" :key="index" class="hover:bg-slate-50 dark:hover:bg-slate-700">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="text-sm font-medium text-slate-900 dark:text-slate-100">{{ member.person }}</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-700">
                      {{ member.team }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="text-sm text-slate-600 dark:text-slate-300">{{ member.role }}</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-slate-900 dark:text-slate-100">
                      <div>{{ formatDate(member.startDate) }}</div>
                      <div class="text-slate-500 dark:text-slate-400">to {{ formatDate(member.endDate) }}</div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="text-sm text-slate-900 dark:text-slate-100">{{ member.days || 0 }}</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="text-sm text-slate-900 dark:text-slate-100">{{ member.assignment || 0 }}%</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="text-sm font-medium text-slate-900 dark:text-slate-100">{{ parseFloat(member.estimatedHours || 0).toFixed(1) }}</span>
                  </td>
                </tr>
              </tbody>
              <tfoot class="bg-slate-50 dark:bg-slate-700 border-t border-slate-200 dark:border-slate-600">
                <tr>
                  <td colspan="6" class="px-6 py-4 text-right text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Total Team Hours:
                  </td>
                  <td class="px-6 py-4 text-sm font-bold text-slate-900 dark:text-slate-100">
                    {{ totalHours.toFixed(1) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Project Notes -->
        <div v-if="budget.notes" class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
          <h2 class="text-slate-900 dark:text-slate-100 text-lg font-semibold mb-4">Notas</h2>
          <div class="prose max-w-none text-slate-700 dark:text-slate-300" v-html="sanitizedNotes"></div>
        </div>

        <!-- Budget Metadata -->
        <!-- Removed the metadata section as the budget information was moved to the top -->
      </div>
    </main>
    
    <!-- Footer -->
    <footer-section />
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { sanitizeTinyMCEContent } from '@/utils/security';
import FooterSection from '../components/ui/Footer.vue';
import { useAuth } from '@/composables/useAuth'; // Import useAuth

export default {
  name: 'TeamBudgetView',
  components: {
    FooterSection
  },
  setup() {
    const toast = useToast();
    const { user, getAuthHeader } = useAuth(); // Use auth composable and getAuthHeader
    return { toast, user, getAuthHeader }; // Return getAuthHeader
  },
  data() {
    return {
      budget: {},
      teamMembers: []
    }
  },
  computed: {
    totalHours() {
      // Use stored totalHours from budget as primary source
      if (this.budget.totalHours && this.budget.totalHours > 0) {
        return this.budget.totalHours;
      }
      
      // Fallback to calculating from team members
      if (!Array.isArray(this.teamMembers)) {
        return 0;
      }
      return this.teamMembers.reduce((total, member) => {
        return total + (parseFloat(member.estimatedHours) || 0);
      }, 0);
    },
    sanitizedNotes() {
      return sanitizeTinyMCEContent(this.budget.notes || '');
    }
  },
  async mounted() {
    const budgetId = this.$route.params.id;
    if (budgetId) {
      await this.loadBudget(budgetId);
    }
  },
  methods: {
    async loadBudget(id) {
      try {
        const headers = this.getAuthHeader(); // Use this.getAuthHeader()
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/api/budgets/${id}`, {
          headers // Add headers to fetch options
        });
        if (response.ok) {
          this.budget = await response.json();
          // Ensure teamMembers is always an array
          this.teamMembers = Array.isArray(this.budget.teamMembers) ? this.budget.teamMembers : [];
        } else {
          if (response.status === 401) {
            this.toast.error("No autorizado para ver este presupuesto.");
            this.$router.push('/login'); // Redirect to login if unauthorized
            return;
          }
          console.error('Failed to load budget:', response.statusText);
          this.toast.error(`Error al cargar el presupuesto: ${response.statusText}`);
          this.$router.push('/budgets');
        }
      } catch (error) {
        console.error('Error loading budget:', error);
        this.toast.error('Error al cargar el presupuesto: ' + error.message);
        this.$router.push('/budgets');
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'Not specified';
      try {
        return new Date(dateString).toLocaleDateString();
      } catch {
        return dateString;
      }
    },
    exportToPDF() {
      const element = document.querySelector('.mx-auto.flex.w-full.flex-col.gap-8');
      this.toast.info("Generando PDF...");
      
      html2canvas(element).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 190;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
        pdf.save(`estimacion-equipo-${this.budget.clientName || 'sin-nombre'}.pdf`);
        this.toast.success("¡PDF generado exitosamente!");
      }).catch(() => {
        this.toast.error("Error al generar el PDF");
      });
    }
  }
}
</script>
